// ═══════════════════════════════════════════════════
//  JepTags Scan Result — Shared Helpers
// ═══════════════════════════════════════════════════

import { AddressObj, Product } from "./types";

// Backend API base
export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "";

/**
 * Smart helper to build correct file URLs.
 * Works for both localhost (dev) and production.
 */
export const getFileUrl = (url?: string): string => {
  if (!url) return "";
  if (url.startsWith("http")) return url;

  const backendBase = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/api\/?$/, "").replace(/\/+$/, "");
  const path = url.startsWith("/") ? url : `/${url}`;

  const fullUrl = `${backendBase}${path}`;
  console.log("📎 getFileUrl() =>", fullUrl);
  return fullUrl;
};

/** Format an address object or string into a single line */
export const fmtAddress = (addr?: AddressObj | string | null): string | undefined => {
  if (!addr) return undefined;
  if (typeof addr === "string") return addr;
  const parts = [
    addr.street,
    addr.city,
    addr.state ?? addr.region,
    addr.postalCode,
    addr.country,
  ].filter(Boolean);
  return addr.formatted || (parts.length ? parts.join(", ") : undefined);
};

/** Pick the best product image URL */
export const pickProductImage = (p?: Product | null): string | undefined => {
  if (!p) return undefined;
  return (
    p.primaryImage ||
    p.images?.find((i) => i.isPrimary)?.url ||
    p.images?.[0]?.url ||
    p.imageUrl
  );
};

/** Format ISO date string to locale string */
export const formatDateTime = (v?: string) => (v ? new Date(v).toLocaleString() : "");

/** Calculate distance between two GPS points (km) */
export const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/** Get tagged locations from scan data (geoStats + supply chain fallback) */
export const getTaggedLocations = (scanData: any): Array<{
  latitude: number;
  longitude: number;
  label: string;
  tagId: string;
}> => {
  const points: Array<{
    latitude: number;
    longitude: number;
    label: string;
    tagId: string;
  }> = [];
  const productId = scanData?.product?.productId || scanData?.tagId;

  if (scanData?.stats?.geoStats?.locations && Array.isArray(scanData.stats.geoStats.locations)) {
    scanData.stats.geoStats.locations.forEach((location: any, idx: number) => {
      if (location.latitude && location.longitude) {
        points.push({
          latitude: location.latitude,
          longitude: location.longitude,
          label: `${location.city || location.name || 'Unknown'} - Tag #${idx + 1}`,
          tagId: location.tagId || `tag_${productId}_${idx + 1}`
        });
      }
    });
  }

  // Fallback to supply chain locations
  if (points.length === 0 && scanData?.traceability?.stages) {
    scanData.traceability.stages.forEach((stage: any, idx: number) => {
      if (stage.location?.latitude && stage.location?.longitude) {
        points.push({
          latitude: stage.location.latitude,
          longitude: stage.location.longitude,
          label: `${stage.name} - ${stage.location.city || 'Unknown'}`,
          tagId: `stage_${idx + 1}`
        });
      }
    });
  }

  return points;
};

/** Format file size for display */
export const formatFileSize = (bytes: number) => {
  if (!bytes) return 'N/A';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export const isImageFile = (mimeType: string) => mimeType?.startsWith('image/');
export const isPdfFile = (mimeType: string) => mimeType === 'application/pdf';